import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAllBeneficiaries } from '../../API/apiService';
import { useLoader } from '../../contexts/loader/loader';
import { buildProtectedImageUrl, fetchWithAuth } from '../../utils/secureMedia';
import './style.scss';

const ALL = '';
const EMPTY_LABEL = 'Sin asignar';

function text(value) {
  return String(value || '').trim();
}

function mediator(beneficiary) {
  const user = beneficiary?.group?.user || beneficiary?.school?.user ||
    beneficiary?.activityGroup?.user || beneficiary?.user;
  return {
    id: text(user?.id || beneficiary?.userId || beneficiary?.user_id) || EMPTY_LABEL,
    name: text(user?.full_name || user?.fullName || user?.name) || EMPTY_LABEL
  };
}

function sponsorEntity(beneficiary) {
  return text(
    beneficiary?.school?.name || beneficiary?.school?.organization ||
    beneficiary?.school?.address || beneficiary?.activityGroup?.activity?.line
  ) || EMPTY_LABEL;
}

function groupName(beneficiary) {
  return text(beneficiary?.group?.code || beneficiary?.activityGroup?.code) || EMPTY_LABEL;
}

function uniqueOptions(items, resolver) {
  const options = new Map();
  items.forEach(item => {
    const option = resolver(item);
    const id = typeof option === 'string' ? option : option.id;
    const name = typeof option === 'string' ? option : option.name;
    options.set(id, { id, name });
  });
  return [...options.values()].sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

function downloadBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

function safeFilePart(value) {
  return text(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_') || 'persona';
}

export default function ReviewerCharacterizations() {
  const { show } = useLoader();
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [selectedMediator, setSelectedMediator] = useState(ALL);
  const [selectedEntity, setSelectedEntity] = useState(ALL);
  const [selectedGroup, setSelectedGroup] = useState(ALL);
  const [search, setSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let active = true;
    async function load() {
      await show(true);
      try {
        const response = await getAllBeneficiaries(false);
        if (active) setBeneficiaries(Array.isArray(response) ? response : []);
      } finally {
        if (active) await show(false);
      }
    }
    load();
    return () => { active = false; };
  }, [show]);

  const mediatorOptions = useMemo(
    () => uniqueOptions(beneficiaries, mediator), [beneficiaries]
  );
  const entitySource = useMemo(
    () => beneficiaries.filter(item => !selectedMediator || mediator(item).id === selectedMediator),
    [beneficiaries, selectedMediator]
  );
  const entityOptions = useMemo(
    () => uniqueOptions(entitySource, sponsorEntity), [entitySource]
  );
  const groupSource = useMemo(
    () => entitySource.filter(item => !selectedEntity || sponsorEntity(item) === selectedEntity),
    [entitySource, selectedEntity]
  );
  const groupOptions = useMemo(
    () => uniqueOptions(groupSource, groupName), [groupSource]
  );
  const filtered = useMemo(() => {
    const needle = text(search).toLocaleLowerCase('es');
    return groupSource.filter(item => {
      if (selectedGroup && groupName(item) !== selectedGroup) return false;
      if (!needle) return true;
      return `${item?.full_name || ''} ${item?.document || ''}`.toLocaleLowerCase('es').includes(needle);
    });
  }, [groupSource, selectedGroup, search]);

  const grouped = useMemo(() => {
    const result = new Map();
    filtered.forEach(person => {
      const mediatorName = mediator(person).name;
      const entityName = sponsorEntity(person);
      const group = groupName(person);
      if (!result.has(mediatorName)) result.set(mediatorName, new Map());
      if (!result.get(mediatorName).has(entityName)) result.get(mediatorName).set(entityName, new Map());
      if (!result.get(mediatorName).get(entityName).has(group)) {
        result.get(mediatorName).get(entityName).set(group, []);
      }
      result.get(mediatorName).get(entityName).get(group).push(person);
    });
    return result;
  }, [filtered]);

  const consentCount = filtered.filter(person => Number(person?.consentImage?.id) > 0).length;

  async function downloadConsent(person) {
    const imageId = Number(person?.consentImage?.id);
    if (!imageId || downloadingId) return;
    setDownloadingId(person.id);
    try {
      const response = await fetchWithAuth(buildProtectedImageUrl(imageId, false, Date.now()));
      if (!response.ok) throw new Error('CONSENT_DOWNLOAD_FAILED');
      downloadBlob(await response.blob(), text(person?.consentImage?.name) ||
        `consentimiento_${safeFilePart(person?.document || person?.full_name)}.pdf`);
    } catch (error) {
      toast('No se pudo descargar el consentimiento', { type: 'error' });
    } finally {
      setDownloadingId(null);
    }
  }

  function resetDependent(setter, value, dependentSetters) {
    setter(value);
    dependentSetters.forEach(reset => reset(ALL));
  }

  return (
    <section className='reviewer-characterizations'>
      <header className='reviewer-characterizations__topbar'>
        <Link to='/revisor'><span className='material-symbols-rounded'>arrow_back</span>Centro de revisión</Link>
        <span><span className='material-symbols-rounded'>visibility</span>Vista de observador</span>
      </header>

      <div className='reviewer-characterizations__shell'>
        <div className='reviewer-characterizations__heading'>
          <div><small>CARACTERIZACIONES</small><h1>Personas y consentimientos</h1>
            <p>Consulta los registros por mediador, Entidad Madrina y grupo.</p></div>
          <div className='reviewer-characterizations__summary'>
            <strong>{filtered.length}</strong><span>personas</span>
            <strong>{consentCount}</strong><span>con consentimiento</span>
          </div>
        </div>

        <section className='reviewer-characterizations__filters' aria-label='Filtros de caracterización'>
          <label>Mediador<select value={selectedMediator} onChange={event => resetDependent(setSelectedMediator, event.target.value, [setSelectedEntity, setSelectedGroup])}>
            <option value=''>Todos los mediadores</option>{mediatorOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select></label>
          <label>Entidad Madrina<select value={selectedEntity} onChange={event => resetDependent(setSelectedEntity, event.target.value, [setSelectedGroup])}>
            <option value=''>Todas las entidades</option>{entityOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select></label>
          <label>Grupo<select value={selectedGroup} onChange={event => setSelectedGroup(event.target.value)}>
            <option value=''>Todos los grupos</option>{groupOptions.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
          </select></label>
          <label>Buscar persona<input value={search} onChange={event => setSearch(event.target.value)} placeholder='Nombre o documento' /></label>
        </section>

        <div className='reviewer-characterizations__results'>
          {[...grouped.entries()].map(([mediatorName, entities]) => (
            <section className='mediator-block' key={mediatorName}>
              <h2><span className='material-symbols-rounded'>person</span>{mediatorName}<small>{[...entities.values()].reduce((total, groups) => total + [...groups.values()].reduce((sum, people) => sum + people.length, 0), 0)} personas</small></h2>
              {[...entities.entries()].map(([entityName, groups]) => (
                <div className='entity-block' key={entityName}><h3>Entidad Madrina: {entityName}</h3>
                  {[...groups.entries()].map(([group, people]) => (
                    <details className='group-block' key={group} open>
                      <summary><span>Grupo {group}</span><small>{people.length} personas</small></summary>
                      <div className='people-table'>
                        <div className='people-table__head'><span>Persona caracterizada</span><span>Documento</span><span>Consentimiento informado</span><span>Detalle</span></div>
                        {people.map(person => {
                          const hasConsent = Number(person?.consentImage?.id) > 0;
                          return <div className='people-table__row' key={person.id}>
                            <strong>{person?.full_name || EMPTY_LABEL}</strong><span>{person?.document || EMPTY_LABEL}</span>
                            <button type='button' className={hasConsent ? 'has-consent' : 'no-consent'} disabled={!hasConsent || downloadingId === person.id} onClick={() => downloadConsent(person)}>
                              <span className='material-symbols-rounded'>{hasConsent ? 'picture_as_pdf' : 'do_not_disturb_on'}</span>{hasConsent ? (downloadingId === person.id ? 'Descargando...' : 'Ver consentimiento') : 'No cargado'}
                            </button>
                            <Link to='/revisor/caracterizaciones/ver-beneficiario' state={{ ...person, backTo: '/revisor/caracterizaciones' }} aria-label={`Ver caracterización de ${person?.full_name}`}><span className='material-symbols-rounded'>visibility</span></Link>
                          </div>;
                        })}
                      </div>
                    </details>
                  ))}
                </div>
              ))}
            </section>
          ))}
          {!filtered.length && <div className='reviewer-characterizations__empty'>No hay personas que coincidan con los filtros seleccionados.</div>}
        </div>
      </div>
    </section>
  );
}
